import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, Info, Check, Circle } from 'lucide-react';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

// Backend poruke → polje uz koje se prikazuju
const fieldForError = (msg = '') => {
  const m = msg.toLowerCase();
  if (m.includes('lozink')) return 'password';
  if (m.includes('email') || m.includes('račun')) return 'email';
  if (m.includes('ime')) return 'full_name';
  return 'form';
};

function Field({ id, label, icon: Icon, error, hint, children }) {
  return (
    <div className="lg-field">
      <label htmlFor={id}>{label}</label>
      <div className={`lg-input${error ? ' lg-input-err' : ''}`}>
        <Icon size={18} className="lg-input-icon" />
        {children}
      </div>
      {error && <div className="lg-err"><AlertCircle size={15} /><span>{error}</span></div>}
      {hint}
    </div>
  );
}

function Login() {
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', full_name: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null); // { field, text, action? }
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [slideH, setSlideH] = useState(null);
  const loginPane = useRef(null);
  const regPane = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('task')) {
      sessionStorage.setItem('redirectAfterLogin', window.location.search);
    }
    if (params.get('error') === 'denied') {
      setError({ field: 'form', text: 'Taj Google račun nije u sustavu. Prijavi se e-adresom koju je dodao administrator ili mu se javi.' });
    }
  }, []);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (error && (error.field === field || error.field === 'form')) setError(null);
  };

  const switchTab = (t) => {
    setTab(t);
    setError(null);
    setSuccess('');
    setShowPw(false);
    setForgotOpen(false);
    setForm(prev => ({ ...prev, password: '' }));
  };

  const redirectAfterLogin = () => {
    const saved = sessionStorage.getItem('redirectAfterLogin');
    sessionStorage.removeItem('redirectAfterLogin');
    window.location.href = saved || '/';
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!form.email) return setError({ field: 'email', text: 'Upiši e-adresu.' });
    if (!form.password) return setError({ field: 'password', text: 'Upiši lozinku.' });
    setLoading(true);
    setError(null);
    setSuccess('');
    try {
      await axios.post('/auth/login', { email: form.email.trim(), password: form.password }, { withCredentials: true });
      redirectAfterLogin();
    } catch (err) {
      const msg = err.response?.data?.error || 'Greška pri prijavi. Pokušaj ponovno.';
      if (msg === 'Pogrešna lozinka.') {
        setError({ field: 'password', text: 'Pogrešna lozinka. Ako si se dosad prijavljivao/la Googleom, koristi gumb ispod.' });
      } else if (msg === 'Račun nije aktiviran.') {
        setError({ field: 'email', text: 'Račun još nije aktiviran.', action: 'register' });
      } else if (msg === 'Email nije pronađen.') {
        setError({ field: 'email', text: 'Ta e-adresa nije u sustavu. Provjeri je ili se javi administratoru.' });
      } else {
        setError({ field: fieldForError(msg), text: msg });
      }
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!form.full_name.trim()) return setError({ field: 'full_name', text: 'Upiši ime i prezime.' });
    if (!form.email) return setError({ field: 'email', text: 'Upiši e-adresu.' });
    if (form.password.length < 8) return setError({ field: 'password', text: 'Lozinka mora imati najmanje 8 znakova.' });
    setLoading(true);
    setError(null);
    try {
      await axios.post('/auth/register', {
        email: form.email.trim(),
        password: form.password,
        full_name: form.full_name.trim(),
      }, { withCredentials: true });
      switchTab('login');
      setSuccess('Račun je aktiviran. Prijavi se novom lozinkom.');
    } catch (err) {
      const msg = err.response?.data?.error || 'Greška pri aktivaciji. Pokušaj ponovno.';
      if (msg.startsWith('Račun već postoji')) {
        setError({ field: 'email', text: 'Račun s tom e-adresom već postoji.', action: 'login' });
      } else {
        setError({ field: fieldForError(msg), text: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const errFor = (field) => {
    if (!error || error.field !== field) return null;
    if (!error.action) return error.text;
    return (
      <>
        {error.text}{' '}
        <button type="button" className="lg-inline-link" onClick={() => switchTab(error.action)}>
          {error.action === 'register' ? 'Aktiviraj račun' : 'Prijavi se'}
        </button>
      </>
    );
  };

  const isLogin = tab === 'login';
  const pwOk = form.password.length >= 8;

  // Visina okvira prati aktivnu stranicu (greške, uputa za lozinku mijenjaju visinu)
  useLayoutEffect(() => {
    const el = (isLogin ? loginPane : regPane).current;
    if (!el) return;
    const measure = () => setSlideH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLogin]);

  return (
    <div className="lg-page">
      <header className="lg-head">
        <img src="/logo_login.png" alt="Grb DVD Bukovac" className="lg-logo" />
        <div className="lg-brand">DVD Bukovac</div>
        <div className="lg-sub">Zagreb · od 1950.</div>
      </header>

      <main className="lg-panel">
        <div className="lg-seg" role="tablist">
          <span className={`lg-seg-pill${isLogin ? '' : ' reg'}`} aria-hidden="true" />
          <button type="button" role="tab" aria-selected={isLogin} className={isLogin ? 'on' : ''} onClick={() => switchTab('login')}>Prijava</button>
          <button type="button" role="tab" aria-selected={!isLogin} className={!isLogin ? 'on' : ''} onClick={() => switchTab('register')}>Aktiviraj račun</button>
        </div>

        {success && <div className="lg-success"><Check size={16} /><span>{success}</span></div>}
        {error?.field === 'form' && <div className="lg-err lg-err-box"><AlertCircle size={15} /><span>{error.text}</span></div>}

        <div className="lg-slide" style={slideH != null ? { height: slideH } : undefined}>
        <div className={`lg-track${isLogin ? '' : ' reg'}`}>
          <div className="lg-pane" ref={loginPane} inert={!isLogin}>
          <form className="lg-form" onSubmit={handleLogin} noValidate>
            <Field id="lg-email" label="E-pošta" icon={Mail} error={errFor('email')}>
              <input id="lg-email" type="email" inputMode="email" autoComplete="username" autoCapitalize="none"
                value={form.email} onChange={e => update('email', e.target.value)} placeholder="tvoj@email.com" />
            </Field>
            <Field id="lg-password" label="Lozinka" icon={Lock} error={errFor('password')}>
              <input id="lg-password" type={showPw ? 'text' : 'password'} autoComplete="current-password"
                value={form.password} onChange={e => update('password', e.target.value)} placeholder="Lozinka" />
              <button type="button" className="lg-eye" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Sakrij lozinku' : 'Prikaži lozinku'}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </Field>
            <button type="submit" className="lg-btn lg-btn-primary" disabled={loading}>
              {loading ? 'Prijava…' : 'Prijavi se'}
            </button>
            <a href="/auth/google" className="lg-btn lg-btn-google">
              <GoogleIcon />Nastavi s Googleom
            </a>
            <div className="lg-bottom">
              <button type="button" className="lg-link" onClick={() => setForgotOpen(v => !v)}>Zaboravljena lozinka?</button>
              {forgotOpen && (
                <div className="lg-note">
                  <Info size={15} />
                  <span>Javi se administratoru DVD-a da ti poništi lozinku, pa je ponovno postavi preko „Aktiviraj račun”. Ako imaš Gmail adresu, možeš se odmah prijaviti Googleom.</span>
                </div>
              )}
            </div>
          </form>
          </div>
          <div className="lg-pane" ref={regPane} inert={isLogin}>
          <form className="lg-form" onSubmit={handleRegister} noValidate>
            <div className="lg-note">
              <Info size={15} />
              <span>Radi samo za e-adresu koju je administrator dodao u sustav.</span>
            </div>
            <Field id="lg-name" label="Ime i prezime" icon={User} error={errFor('full_name')}>
              <input id="lg-name" type="text" autoComplete="name"
                value={form.full_name} onChange={e => update('full_name', e.target.value)} placeholder="Ivan Horvat" />
            </Field>
            <Field id="lg-remail" label="E-pošta" icon={Mail} error={errFor('email')}>
              <input id="lg-remail" type="email" inputMode="email" autoComplete="username" autoCapitalize="none"
                value={form.email} onChange={e => update('email', e.target.value)} placeholder="tvoj@email.com" />
            </Field>
            <Field id="lg-newpw" label="Nova lozinka" icon={Lock} error={errFor('password')}
              hint={!errFor('password') && (
                <div className={`lg-check${pwOk ? ' ok' : ''}`}>
                  {pwOk ? <Check size={14} /> : <Circle size={12} />}
                  <span>{pwOk || !form.password ? 'Najmanje 8 znakova' : `Najmanje 8 znakova (još ${8 - form.password.length})`}</span>
                </div>
              )}>
              <input id="lg-newpw" type={showPw ? 'text' : 'password'} autoComplete="new-password"
                value={form.password} onChange={e => update('password', e.target.value)} placeholder="Nova lozinka" />
              <button type="button" className="lg-eye" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Sakrij lozinku' : 'Prikaži lozinku'}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </Field>
            <button type="submit" className="lg-btn lg-btn-primary" disabled={loading}>
              {loading ? 'Aktivacija…' : 'Aktiviraj račun'}
            </button>
          </form>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}

export default Login;
